import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import ModuleLayout from '@/components/admin/ModuleLayout';
import { SectionHeader } from '@/components/cccd/SectionHeader';
import Alert from '@/components/ui/alert/Alert';
import { inputCls, labelCls, btnSave, btnSecondary } from '@/components/admin/FormComponents';
import {
  PARAMETER_TYPES,
  PARAMETER_TYPE_LABELS,
  PARAMETER_TYPE_CONFIG_FIELDS,
  CONFIG_FIELD_LABELS,
  CONFIG_FIELD_HELP,
  type CccdParameterType,
} from '@/lib/cccdScoringParameters';
import { PARAMETER_TYPE_PREVIEWS } from '@/lib/cccdScoringPreview';
import { cccdScoring } from '@/lib/api/cccdScoringClient';
import { EMPTY_PARAMETER_CONFIG, toParameterPayload, type ParameterConfigValues } from '@/components/cccd/ParameterConfigFields';
import { getValidationErrorMessage } from '@/lib/errors';
import { toastSuccess } from '@/lib/toast';
import { LoadState } from "@/components/ui/LoadState"

// Un ícono simple por tipo (mismo trazo que el resto de SectionHeader —
// sin librería externa, ver craft-floor: un solo sistema de íconos).
const TYPE_ICONS: Record<CccdParameterType, React.ReactNode> = {
  numeric_value: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M5 9h14M5 15h14M10 4 8 20M16 4l-2 16" /></svg>
  ),
  calculated_height: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 20 14 4l6 16" /><path d="M4 20h16" /><path d="M9 20 14 11" /></svg>
  ),
  height_with_max_error: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1.2" fill="currentColor" stroke="none" /></svg>
  ),
  height_with_perfect_band: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="3.2" fill="currentColor" stroke="none" /></svg>
  ),
  flight_time: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="13" r="8" /><path d="M12 9v4l3 2M10 2h4" /></svg>
  ),
  yes_no_parameter: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
  ),
};

function parameterToValues(p: { name: string; parameterType: CccdParameterType; unitOfMeasure: string | null; maxScore: number | null; targetHeight: number | null; maxError: number | null; perfectBand: number | null; minTime: number | null; maxTime: number | null; tolerance: number | null; maxValue: number | null }): ParameterConfigValues {
  return {
    name: p.name,
    parameterType: p.parameterType,
    unitOfMeasure: p.unitOfMeasure ?? '',
    maxScore: p.maxScore?.toString() ?? '',
    targetHeight: p.targetHeight?.toString() ?? '',
    maxError: p.maxError?.toString() ?? '',
    perfectBand: p.perfectBand?.toString() ?? '',
    minTime: p.minTime?.toString() ?? '',
    maxTime: p.maxTime?.toString() ?? '',
    tolerance: p.tolerance?.toString() ?? '',
    maxValue: p.maxValue?.toString() ?? '',
  };
}

// Selector de tipo como tarjetas (no un <select>) — pensado para personal no
// técnico del CCCD: cada tarjeta explica en una frase qué hace el tipo,
// antes de elegirlo (cccd-scoring-ux-redesign).
function ParameterTypePicker({ value, onChange }: { value: CccdParameterType; onChange: (t: CccdParameterType) => void }) {
  return (
    <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
      {PARAMETER_TYPES.map((type) => {
        const selected = value === type;
        return (
          <button
            key={type}
            type="button"
            onClick={() => onChange(type)}
            className={`flex items-start gap-3 rounded-xl border p-3.5 text-left transition-colors ${
              selected
                ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/[0.08]'
                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-white/[0.03]'
            }`}
          >
            <div className={`flex size-9 shrink-0 items-center justify-center rounded-lg ${selected ? 'bg-brand-500 text-white' : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'}`}>
              {TYPE_ICONS[type]}
            </div>
            <div>
              <p className={`text-[13px] font-semibold ${selected ? 'text-brand-700 dark:text-brand-300' : 'text-gray-800 dark:text-white/90'}`}>{PARAMETER_TYPE_LABELS[type]}</p>
              <p className="mt-0.5 text-[12px] leading-snug text-gray-500 dark:text-gray-400">{PARAMETER_TYPE_PREVIEWS[type].cardBlurb}</p>
            </div>
          </button>
        );
      })}
    </div>
  );
}

// Panel de ejemplo en vivo — recalcula con los valores reales que el usuario
// va llenando (o valores de ejemplo razonables si aún están vacíos), para
// que alguien no técnico entienda qué va a pasar ANTES de guardar.
function LiveExamplePanel({ values }: { values: ParameterConfigValues }) {
  const preview = PARAMETER_TYPE_PREVIEWS[values.parameterType];
  const lines = preview.lines(values);

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03] lg:sticky lg:top-6">
      <div className="mb-3 flex items-center gap-2.5">
        <div className="flex size-8 items-center justify-center rounded-lg bg-brand-50 text-brand-500 dark:bg-brand-500/[0.12] dark:text-brand-400">
          {TYPE_ICONS[values.parameterType]}
        </div>
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Ejemplo en vivo</p>
          <p className="text-[14px] font-semibold text-gray-800 dark:text-white/90">{PARAMETER_TYPE_LABELS[values.parameterType]}</p>
        </div>
      </div>
      <p className="text-[13px] leading-relaxed text-gray-600 dark:text-gray-300">{preview.howItWorks}</p>
      <div className="mt-4 flex flex-col gap-2.5">
        {lines.map((line, i) => (
          <div key={i} className="rounded-lg bg-gray-25 p-3 dark:bg-white/[0.02]">
            <p className="text-[12px] text-gray-500 dark:text-gray-400">{line.input}</p>
            <p className="mt-0.5 text-[13px] font-semibold text-gray-800 dark:text-white/90">→ {line.result}</p>
          </div>
        ))}
      </div>
      <p className="mt-3.5 text-[11px] text-gray-500 dark:text-gray-400">Este ejemplo se recalcula con los números que vayas llenando a la izquierda.</p>
    </div>
  );
}

export default function ScoringParameterFormPage() {
  const { parameterId } = useParams<{ parameterId: string }>();
  const isEdit = Boolean(parameterId);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [values, setValues] = useState<ParameterConfigValues>(EMPTY_PARAMETER_CONFIG);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!parameterId) return;
    setLoading(true);
    setError('');
    try {
      const parameters = await cccdScoring.listParameters();
      const found = parameters.find((p) => p.id === parameterId);
      if (!found) {
        setError('Parámetro no encontrado.');
        return;
      }
      setValues(parameterToValues(found));
    } catch (err) {
      setError(getValidationErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [parameterId]);

  useEffect(() => { void load(); }, [load]);

  function goBack() {
    navigate('/cccd/scoring-parameters');
  }

  function set<K extends keyof ParameterConfigValues>(key: K, v: ParameterConfigValues[K]) {
    setValues((prev) => ({ ...prev, [key]: v }));
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (isEdit && parameterId) {
        await cccdScoring.updateParameter(parameterId, toParameterPayload(values));
        toastSuccess('Parámetro actualizado.');
      } else {
        await cccdScoring.createParameter(toParameterPayload(values));
        toastSuccess('Parámetro creado.');
      }
      qc.invalidateQueries({ queryKey: ['scoring-parameters'] });
      goBack();
    } catch (err) {
      setError(getValidationErrorMessage(err));
      setSaving(false);
    }
  }

  const activeFields = PARAMETER_TYPE_CONFIG_FIELDS[values.parameterType];

  return (
    <ModuleLayout
      breadcrumbs={[
        { label: 'SateLab', to: '/' },
        { label: 'CCCD', to: '/cccd' },
        { label: 'Parámetros de evaluación', to: '/cccd/scoring-parameters' },
        isEdit ? 'Editar' : 'Nuevo',
      ]}
      title={isEdit ? 'Editar parámetro de evaluación' : 'Nuevo parámetro de evaluación'}
      subtitle="Define qué se califica y cómo se calcula el puntaje — a la derecha verás un ejemplo real con tus propios números, sin necesidad de guardarlo primero."
      tabs={[]}
      metrics={[]}
    >
      {loading ? (
        <LoadState />
      ) : (
        <form onSubmit={onSubmit}>
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-5">
            <div className="flex flex-col gap-5 lg:col-span-3">
              {isEdit && (
                <Alert
                  variant="warning"
                  title="Este parámetro es global"
                  message="Los cambios afectan a todas las categorías que lo usan. Al guardar, los puntajes ya capturados se recalculan con la nueva configuración."
                />
              )}
              <SectionHeader
                title="¿Qué vas a calificar?"
                subtitle="El nombre es lo que verá el encargado al cargar resultados"
                icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 7h16M4 12h10M4 17h7" /></svg>}
                className="space-y-3"
              >
                <div>
                  <label htmlFor="scoring-parameter-name" className={labelCls}>Nombre del parámetro *</label>
                  <input
                    id="scoring-parameter-name"
                    required
                    autoFocus
                    className={inputCls}
                    placeholder="Ej. Presentación oral, Altura alcanzada..."
                    value={values.name}
                    onChange={(e) => set('name', e.target.value)}
                  />
                </div>
                <div>
                  <label htmlFor="scoring-parameter-unit" className={labelCls}>Unidad de medida (opcional)</label>
                  <input id="scoring-parameter-unit" className={inputCls} placeholder="m, s..." value={values.unitOfMeasure} onChange={(e) => set('unitOfMeasure', e.target.value)} />
                </div>
              </SectionHeader>

              <SectionHeader
                title="¿Cómo se va a calificar?"
                subtitle="Elige el tipo que más se parezca a cómo el jurado va a evaluar esto"
                icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M9 21V9" /></svg>}
              >
                <ParameterTypePicker value={values.parameterType} onChange={(t) => set('parameterType', t)} />
              </SectionHeader>

              {activeFields.length > 0 && (
                <SectionHeader
                  title="Configuración del puntaje"
                  subtitle="Estos números son los que definen cuántos puntos gana un equipo"
                  icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v20M2 12h20" /></svg>}
                  className="space-y-3.5"
                >
                  {activeFields.map((field) => (
                    <div key={field}>
                      <label className={labelCls}>{CONFIG_FIELD_LABELS[field]}</label>
                      <input
                        type="number"
                        className={inputCls}
                        value={values[field]}
                        onChange={(e) => set(field, e.target.value)}
                      />
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{CONFIG_FIELD_HELP[field]}</p>
                    </div>
                  ))}
                </SectionHeader>
              )}
            </div>

            <div className="lg:col-span-2">
              <LiveExamplePanel values={values} />
            </div>
          </div>

          {error ? (
            <p role="alert" aria-live="assertive" className="mt-5 text-sm font-medium text-error-500">{error}</p>
          ) : null}

          <div className="mt-5 flex justify-end gap-2 border-t border-gray-100 pt-5 dark:border-gray-800">
            <button type="button" onClick={goBack} className={btnSecondary}>Cancelar</button>
            <button type="submit" disabled={!values.name.trim() || saving} className={btnSave}>
              {saving ? 'Guardando...' : isEdit ? 'Guardar cambios' : 'Crear parámetro'}
            </button>
          </div>
        </form>
      )}
    </ModuleLayout>
  );
}
